import { useEffect, useRef } from "react";

/**
 * Kosovo node cluster — central hub with orbiting opportunity-area nodes.
 * Labels use measureText to stay inside the canvas; positions are tuned so
 * the longest labels (Public Modernization, Natural Resources, Energy &
 * Infrastructure) sit on the diagonals where they have horizontal room.
 */
export default function NodeCluster({ height = 480 }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize(); window.addEventListener("resize", resize);

    // Layout: hex-ish arrangement, longest labels go on the diagonals where
    // there's diagonal slack; side labels stay short.
    const labels = [
      { x: 0.5,  y: 0.5,  l: "PRISTINA", main: true },
      { x: 0.30, y: 0.18, l: "Digital Infrastructure" },
      { x: 0.70, y: 0.18, l: "Trade Coordination" },
      { x: 0.78, y: 0.50, l: "Public Modernization" },
      { x: 0.70, y: 0.82, l: "Defense Support" },
      { x: 0.30, y: 0.82, l: "Energy & Infrastructure" },
      { x: 0.22, y: 0.50, l: "Natural Resources" },
    ];

    const PAD = 10; // safety padding from canvas edges

    let t = 0; let raf;
    const draw = () => {
      const w = c.clientWidth, h = c.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const cx = labels[0].x * w, cy = labels[0].y * h;

      labels.slice(1).forEach((n, i) => {
        const nx = n.x * w, ny = n.y * h;

        // connector line
        const grd = ctx.createLinearGradient(cx, cy, nx, ny);
        grd.addColorStop(0, "rgba(157,180,255,0.7)");
        grd.addColorStop(1, "rgba(157,180,255,0.05)");
        ctx.strokeStyle = grd; ctx.lineWidth = 0.7;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(nx, ny); ctx.stroke();

        // travelling pulse
        const tt = ((t / 100 + i * 0.13) % 1);
        const px = cx + (nx - cx) * tt, py = cy + (ny - cy) * tt;
        ctx.fillStyle = "rgba(232,236,241,0.95)";
        ctx.beginPath(); ctx.arc(px, py, 1.6, 0, Math.PI * 2); ctx.fill();

        // node
        ctx.fillStyle = "#E8ECF1";
        ctx.beginPath(); ctx.arc(nx, ny, 3, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "rgba(157,180,255,0.18)";
        ctx.beginPath(); ctx.arc(nx, ny, 9, 0, Math.PI * 2); ctx.fill();

        // label — safe placement
        ctx.font = "500 11px 'Inter', system-ui";
        ctx.fillStyle = "rgba(232,236,241,0.78)";
        const tw = ctx.measureText(n.l).width;

        const wantRightSide = n.x >= 0.5;
        let drawX;
        let align;
        if (wantRightSide) {
          // try left-align starting at nx + 12
          if (nx + 12 + tw <= w - PAD) {
            align = "left"; drawX = nx + 12;
          } else {
            // not enough room to the right — flip to right-align ending at canvas edge
            align = "right"; drawX = w - PAD;
          }
        } else {
          // try right-align ending at nx - 12
          if (nx - 12 - tw >= PAD) {
            align = "right"; drawX = nx - 12;
          } else {
            align = "left"; drawX = PAD;
          }
        }
        ctx.textAlign = align;
        // vertical: keep on the same row as the node, but if it would clash
        // with the central PRISTINA label area, drop the label slightly below
        const dyOffset = Math.abs(ny - cy) < 24 ? 22 : 4;
        ctx.fillText(n.l, drawX, ny + dyOffset);
        ctx.textAlign = "start";
      });

      // central hub
      const pulse = 12 + Math.sin(t / 25) * 4;
      ctx.fillStyle = "rgba(157,180,255,0.18)";
      ctx.beginPath(); ctx.arc(cx, cy, 18 + pulse, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "rgba(157,180,255,0.4)";
      ctx.beginPath(); ctx.arc(cx, cy, 12, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#E8ECF1";
      ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "rgba(232,236,241,0.95)";
      ctx.font = "600 11px 'Inter', system-ui";
      ctx.textAlign = "center";
      ctx.fillText("PRISTINA · XK", cx, cy - 18);
      ctx.textAlign = "start";

      t += 1; raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="w-full block" style={{ width: "100%", height }} />;
}
